import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, from, switchMap, take } from 'rxjs';
import { Document } from '../models/document';


@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private readonly apiUrl = '/api/Document/';


  private fileSubject = new BehaviorSubject<Uint8Array | null>(null);
  file$ = this.fileSubject.asObservable();

  private fileBlobSubject = new BehaviorSubject<Blob | null>(null);
  fileBlob$ = this.fileBlobSubject.asObservable();

  private documentsSubject = new BehaviorSubject<Document[] | null>(null);
  documents$ = this.documentsSubject.asObservable();

  private initiatorDocumentSubject = new BehaviorSubject<Document | null>(null);
  initiatorDocument$ = this.initiatorDocumentSubject.asObservable();

  private fileCache = new Map<string, Uint8Array>();
  private fileBlobCache = new Map<string, Blob>();
  private readonly maxCacheSize = 100;

  constructor(private httpClient: HttpClient) {
  }

  getDocuments(SubSectionId: string): void {
    this.httpClient.get<Document[]>(this.apiUrl + SubSectionId + '/get').pipe(take(1)).subscribe({
      next: (result) => this.documentsSubject.next(result),
      error: (err) => console.error('Ошибка загрузки документов', err)
    });
  }

  addDocument(formData: FormData): void {
    this.httpClient.post<Document>(this.apiUrl + 'create', formData).subscribe()
  }

  deleteDocument(id: string, subSectionId: string): void {
    this.httpClient.delete(this.apiUrl + id + '/delete').subscribe();
  }

  loadFile(id: string): void {

    if (this.fileCache.has(id)) {
      // Всегда передаём свежую копию: ngx-extended-pdf-viewer извлекает внутренний
      // ArrayBuffer и передаёт его в Web Worker PDF.js через postMessage (transfer),
      // что делает оригинальный буфер недействительным. Новая копия Uint8Array имеет
      // собственный независимый буфер, поэтому эталонная копия в кеше остаётся целой
      // для последующих загрузок.
      this.fileSubject.next(new Uint8Array(this.fileCache.get(id)!));
      return;
    }

    this.httpClient.get(this.apiUrl + id + '/getFileById', { responseType: 'blob' }).pipe(
      take(1),
      switchMap(file => from(file.arrayBuffer()))
    ).subscribe({
      next: (buffer) => {
        const data = new Uint8Array(buffer);
        if (this.fileCache.size >= this.maxCacheSize) {
          const oldestKey = this.fileCache.keys().next().value!;
          this.fileCache.delete(oldestKey);
        }
        // Сохраняем в кеше отдельную копию, чтобы передача буфера в viewer не делала
        // эталонную копию в кеше недействительной — она остаётся пригодной для
        // последующих отображений.
        this.fileCache.set(id, new Uint8Array(data));
        this.fileSubject.next(new Uint8Array(data));
      },
      error: (err) => console.error('Ошибка загрузки файла', err),
    });
  }

  loadOriginalFile(id: string): void {
    if (this.fileBlobCache.has(id)) {
      this.fileBlobSubject.next(this.fileBlobCache.get(id)!);
      return;
    }
    this.httpClient.get(this.apiUrl + id + '/getOriginalFileById', { responseType: 'blob' }).pipe(take(1)).subscribe({
      next: (file) => {
        if (this.fileBlobCache.size >= this.maxCacheSize) {
          const oldestKey = this.fileBlobCache.keys().next().value!;
          this.fileBlobCache.delete(oldestKey);
        }
        this.fileBlobCache.set(id, file);
        this.fileBlobSubject.next(file);
      }
    })
  }

  reconvertDocument(id: string): void {
    this.httpClient.post(this.apiUrl + id + '/reconvert', null).subscribe({
      error: (err) => {
        console.error('Ошибка переконвертации документа', err);
        // SignalR will handle the success notification to all clients
      }
    });
  }

  reset() {
    this.documentsSubject.next(null);
    this.fileSubject.next(null);
    this.fileCache.clear();
    this.fileBlobCache.clear();
  }

  addLocal(document: Document, isInitiator: boolean): void {
    const documents = this.documentsSubject.value;
    if (!documents) return;

    this.documentsSubject.next([...documents, document].sort((a, b) => a.name.localeCompare(b.name)));

    if (isInitiator) {
      this.initiatorDocumentSubject.next(document);
    }
  }

  updateLocal(document: Document): void {

    console.log(document);

    const documentId = document.id;

    if (this.fileCache.has(documentId)) {
      this.fileCache.delete(documentId);
    }
    this.fileBlobCache.delete(documentId);


    const documents = this.documentsSubject.value;
    if (!documents) return;

    this.documentsSubject.next(
      documents.map(d => d.id === document.id ? document : d).sort((a, b) => a.name.localeCompare(b.name)));
  }

  removeLocal(documentId: string): void {
    const documents = this.documentsSubject.value;
    if (!documents) return;

    this.documentsSubject.next(
      documents.filter(d => d.id !== documentId).sort((a, b) => a.name.localeCompare(b.name)));

    if (this.fileCache.has(documentId)) {
      this.fileCache.delete(documentId);
    }
    this.fileBlobCache.delete(documentId);

    if (this.fileSubject.value) {
      this.fileSubject.next(null);
    }
  }
}
